// Lender Offer Form Constants

export const LOAN_PRODUCT_TYPES = {
  en: {
    dette_senior_lbo: 'Senior Debt (LBO)',
    unitranche: 'Unitranche',
    pret_commercial: 'Commercial Loan',
    sba_loan: 'SBA Loan',
    abl: 'Asset-Based Lending (ABL)',
    pret_vendeur: 'Seller Financing (Subordinated Debt)',
    mezzanine: 'Mezzanine',
    equipment_lease: 'Equipment Lease',
    real_estate_loan: 'Real Estate Loan',
    bridge: 'Bridge Loan',
    other_loan: 'Other Loan Type'
  },
  fr: {
    dette_senior_lbo: 'Dette Senior (LBO)',
    unitranche: 'Unitranche',
    pret_commercial: 'Prêt Commercial',
    sba_loan: 'SBA Loan',
    abl: 'Asset-Based Lending (ABL)',
    pret_vendeur: 'Prêt Vendeur (Dette Subordonnée)',
    mezzanine: 'Mezzanine',
    equipment_lease: 'Equipment Lease',
    real_estate_loan: 'Real Estate Loan',
    bridge: 'Bridge Loan',
    other_loan: 'Autre Type de Prêt'
  }
};

export const LENDER_TYPES = {
  en: {
    sba_preferred_lender: 'SBA Preferred Lender',
    traditional_bank: 'Traditional Bank',
    non_bank_lender: 'Non-Bank Lender',
    fintech_lender: 'FinTech Lender',
    private_lender: 'Private Lender',
    private_equity: 'Private Equity (PE)',
    family_office: 'Family Office',
    equipment_financing: 'Equipment Financing',
    asset_based_lender: 'Asset-Based Lender (ABL)',
    other_lender: 'Other Lender Type'
  },
  fr: {
    sba_preferred_lender: 'SBA Preferred Lender',
    traditional_bank: 'Banque Traditionnelle',
    non_bank_lender: 'Prêteur Non-Bancaire',
    fintech_lender: 'FinTech Lender',
    private_lender: 'Prêteur Privé',
    private_equity: 'Private Equity (PE)',
    family_office: 'Family Office',
    equipment_financing: 'Financement d\'Équipement',
    asset_based_lender: 'Asset-Based Lender (ABL)',
    other_lender: 'Autre Type de Prêteur'
  }
};

export const COLLATERAL_OPTIONS = {
  en: {
    all_business_assets_first_lien: 'All Business Assets (First Lien)',
    accounts_receivable: 'Accounts Receivable',
    inventory: 'Inventory',
    equipment_and_machinery: 'Equipment & Machinery',
    personal_real_estate: 'Personal Real Estate',
    personal_guarantee_secured: 'Personal Guarantee (Secured)',
    personal_guarantee_unsecured: 'Personal Guarantee (Unsecured)',
    business_real_estate_first_lien: 'Business Real Estate (First Lien)',
    intellectual_property: 'Intellectual Property',
    cash_on_deposit: 'Cash on Deposit'
  },
  fr: {
    all_business_assets_first_lien: 'Tous les Actifs (Premier Rang)',
    accounts_receivable: 'Comptes Clients',
    inventory: 'Inventaire',
    equipment_and_machinery: 'Équipement & Machinerie',
    personal_real_estate: 'Immobilier Personnel',
    personal_guarantee_secured: 'Garantie Personnelle (Sécurisée)',
    personal_guarantee_unsecured: 'Garantie Personnelle (Non Sécurisée)',
    business_real_estate_first_lien: 'Immobilier Commercial (Premier Rang)',
    intellectual_property: 'Propriété Intellectuelle',
    cash_on_deposit: 'Dépôts en Espèces'
  }
};

export const COUNTRIES_LIST = [
  { code: 'US', name: { en: 'United States', fr: 'États-Unis' } },
  { code: 'CA', name: { en: 'Canada', fr: 'Canada' } },
  { code: 'GB', name: { en: 'United Kingdom', fr: 'Royaume-Uni' } },
  { code: 'DE', name: { en: 'Germany', fr: 'Allemagne' } },
  { code: 'FR', name: { en: 'France', fr: 'France' } },
  { code: 'JP', name: { en: 'Japan', fr: 'Japon' } },
  { code: 'SG', name: { en: 'Singapore', fr: 'Singapour' } },
  { code: 'NL', name: { en: 'Netherlands', fr: 'Pays-Bas' } },
  { code: 'LU', name: { en: 'Luxembourg', fr: 'Luxembourg' } },
  { code: 'CH', name: { en: 'Switzerland', fr: 'Suisse' } },
  { code: 'BE', name: { en: 'Belgium', fr: 'Belgique' } },
  { code: 'IT', name: { en: 'Italy', fr: 'Italie' } },
  { code: 'AU', name: { en: 'Australia', fr: 'Australie' } },
  { code: 'SE', name: { en: 'Sweden', fr: 'Suède' } },
  { code: 'BR', name: { en: 'Brazil', fr: 'Brésil' } },
  { code: 'IN', name: { en: 'India', fr: 'Inde' } }
];

export const LENDER_OFFER_TRANSLATIONS = {
  en: {
    // Header
    title: 'Lender Offer Form',
    subtitle: 'AcquizGroup - Version Master 3.0',
    
    // Steps
    steps: {
      deal: 'Deal',
      lender: 'Lender',
      terms: 'Terms',
      fees: 'Fees',
      covenants: 'Covenants',
      ablSub: 'ABL/Sub',
      credit: 'Credit',
      review: 'Review'
    },
    
    // Navigation
    previous: '← Previous',
    next: 'Next →',
    submit: 'Submit Offer',
    saveDraft: '💾 Save Draft',
    deleteDraft: '🗑️',
    lastSaved: 'Last saved:',
    
    // Step 1: Deal Summary
    step1Title: '📊 Complete Deal Summary',
    step1Subtitle: 'Information extracted from the seller\'s submission',
    dealId: 'Deal ID',
    industry: 'Industry',
    location: 'Location',
    askingPrice: 'Asking Price',
    yearsInOperation: 'Years in Operation',
    sellerUrgency: 'Seller Urgency',
    urgencyHigh: 'High',
    urgencyMedium: 'Medium',
    urgencyLow: 'Low',
    financialMetrics: '💰 Financial Metrics',
    averageSDE: 'Average SDE',
    maintenanceCAPEX: 'Maintenance CAPEX',
    dscrSystem: 'DSCR (System)',
    adjustedEBITDA: 'Adjusted EBITDA (Last 3 Years):',
    riskFactors: '⚠️ Risk Factors',
    mainWeakness: 'Main Weakness:',
    customerConcentration: 'Top Customer Concentration:',
    managementScore: 'Management Score:',
    projectedGrowth: 'Projected Growth:',
    existingDebt: '💳 Existing Debt to Refinance',
    description: 'Description',
    amount: 'Amount',
    secured: 'Secured',
    yes: 'Yes',
    no: 'No',
    
    // Step 2: Lender Info
    step2Title: 'Step 2: Lender Information',
    institutionInfo: '2.1 - Institution Information',
    institutionName: 'Institution Name',
    licenseNMLS: 'License # / NMLS',
    representativeInfo: '2.2 - Representative Information',
    representativeName: 'Representative Name',
    phone: 'Phone',
    email: 'Email',
    
    // Step 3: Loan Terms
    step3Title: 'Step 3: Loan Type & Terms',
    lenderTypeProduct: '3.1 - Lender Type & Product',
    lenderType: 'Lender Type',
    loanProductType: 'Loan Product Type',
    selectOption: 'Select...',
    commercialTerms: '3.2 - Commercial Terms',
    loanAmountOffered: 'Loan Amount Offered ($)',
    loanTerm: 'Term (months)',
    rateType: 'Rate Type',
    rateFixed: 'Fixed',
    rateFloating: 'Floating',
    rateIndexed: 'Indexed (SOFR+)',
    baseRate: 'Base Rate (%)',
    marginBps: 'Margin (bps)',
    marginNote: 'If indexed (SOFR + X bps)',
    floorRate: 'Floor Rate (%)',
    amortizationType: 'Amortization Type',
    amortLinear: 'Linear',
    amortInFine: 'In Fine (Balloon)',
    amortCustom: 'Custom Schedule',
    prepaymentPenalty: 'Prepayment Penalty',
    prepaymentPlaceholder: 'Ex: 2% if repaid within first 3 years',
    
    // Step 4: Fees
    step4Title: 'Step 4: Fees',
    structuredFees: 'Structured Fees',
    arrangementFee: 'Arrangement Fee (%)',
    feeOfLoan: '% of loan amount',
    drawFeeABL: 'ABL Draw Fee (%)',
    drawFeeNote: 'If ABL, % per draw',
    exitFee: 'Exit Fee (%)',
    exitFeeNote: 'If Mezzanine/LBO',
    otherFees: 'Other Fees',
    feeDescription: 'Description',
    feeAmount: 'Amount',
    feeType: 'Type',
    feeFixed: 'Fixed ($)',
    feePercentage: '% of loan',
    addFee: '+ Add Fee',
    removeFee: 'Remove',
    
    // Step 5: Covenants
    step5Title: 'Step 5: Covenants, Ratios & Collateral',
    maintenanceCovenants: 'Maintenance Covenants',
    maintenanceCovenantsRequired: 'Maintenance covenants required? (Quarterly test)',
    netDebtToEBITDA: 'Net Debt / EBITDA Max',
    leverageRatio: 'Maximum leverage ratio',
    dscrMinimum: 'Minimum DSCR',
    dscrCalculationBase: 'DSCR Calculation Base',
    dscrCFADS: 'CFADS (Cash Flow After Debt Service)',
    dscrSDE: 'SDE (Seller\'s Discretionary Earnings)',
    dscrEBITDA: 'EBITDA',
    dividendRestrictions: 'Dividend Restrictions',
    dividendPlaceholder: 'Ex: No distribution if DSCR < 1.5x',
    collateral: 'Collateral',
    collateralRequired: 'Collateral required?',
    collateralTypes: 'Collateral Types',
    personalGuaranteeRequired: 'Personal Guarantee (PG) required?',
    realEstateConditioned: 'Offer conditioned on real estate mortgage?',
    
    // Step 6: ABL & Subordinated
    step6Title: 'Step 6: ABL & Subordinated Debt',
    ablSection: 'ABL (Asset-Based Lending)',
    isABLOffer: 'This is an ABL offer?',
    arEligibility: 'Eligible Accounts Receivable (%)',
    inventoryEligibility: 'Eligible Inventory (%)',
    cashSweep: 'Cash Sweep required?',
    subordinatedDebt: 'Subordinated Debt (Seller Financing / Mezzanine)',
    isSubordinatedDebt: 'This is subordinated debt?',
    subordinationToSenior: 'Subordinated to senior debt?',
    gracePeriod: 'Grace Period (months)',
    gracePeriodNote: 'Interest grace period',
    balloonYear: 'Balloon Year',
    balloonAmount: 'Balloon Amount ($)',
    
    // Step 7: Credit
    step7Title: 'Step 7: Credit Requirements & International',
    nationalCreditScore: 'National Credit Score',
    creditScoreRequired: 'National credit score required?',
    minimumScore: 'Minimum Score',
    ficoNote: 'Typical FICO score: 300-850',
    foreignCreditScore: 'Foreign Credit Score',
    acceptForeignScores: 'Do you accept foreign credit scores?',
    foreignMinimumScore: 'Foreign Minimum Score',
    foreignScoreNote: 'Minimum accepted score (varies by country)',
    acceptedCountries: 'Accepted Countries (ISO codes)',
    selectCountries: 'Select countries whose credit scores you accept:',
    countriesSelected: 'country(ies) selected',
    foreignCollateral: 'Foreign Collateral',
    acceptForeignCollateral: 'Do you accept foreign collateral?',
    foreignCollateralDetails: 'Details',
    foreignCollateralPlaceholder: 'Specify accepted types, countries, conditions...',
    
    // Step 8: Review
    step8Title: 'Step 8: Timeline & Final Review',
    timeline: 'Timeline',
    closingTimeline: 'Closing Timeline (days)',
    offerExpiration: 'Offer Expiration Date',
    offerSummary: '📋 Your Offer Summary',
    lender: 'Lender',
    amountOffered: 'Amount Offered',
    rate: 'Rate',
    type: 'Type',
    product: 'Product',
    term: 'Term',
    months: 'months',
    toFill: '(To fill)',
    toSelect: '(To select)',
    confirmSubmission: 'I confirm this offer is complete and accurate',
    confirmNote: 'My institution is ready to proceed with these terms.',
    
    // Validation errors
    errors: {
      institutionNameRequired: 'Institution name required',
      representativeNameRequired: 'Representative name required',
      emailRequired: 'Email required',
      phoneRequired: 'Phone required',
      lenderTypeRequired: 'Lender type required',
      loanProductRequired: 'Loan product type required',
      loanAmountRequired: 'Loan amount required',
      interestRateRequired: 'Interest rate required'
    },
    
    // Notifications
    draftSaved: '✅ Draft saved!',
    draftDeleted: '🗑️ Draft deleted',
    draftRestored: '📝 Draft restored!',
    saveError: '❌ Save error',
    confirmDeleteDraft: 'Delete saved draft?',
    draftFoundRestore: 'A saved draft was found. Do you want to restore it?',
    offerSubmitted: '✅ Offer submitted successfully!'
  },
  fr: {
    // Header
    title: 'Formulaire d\'Offre Prêteur',
    subtitle: 'AcquizGroup - Version Master 3.0',
    
    // Steps
    steps: {
      deal: 'Deal',
      lender: 'Prêteur',
      terms: 'Termes',
      fees: 'Frais',
      covenants: 'Covenants',
      ablSub: 'ABL/Sub',
      credit: 'Crédit',
      review: 'Révision'
    },
    
    // Navigation
    previous: '← Précédent',
    next: 'Suivant →',
    submit: 'Soumettre l\'Offre',
    saveDraft: '💾 Enregistrer le brouillon',
    deleteDraft: '🗑️',
    lastSaved: 'Dernière sauvegarde:',
    
    // Step 1: Deal Summary
    step1Title: '📊 Résumé Complet du Deal',
    step1Subtitle: 'Informations extraites de la soumission du vendeur',
    dealId: 'Deal ID',
    industry: 'Industrie',
    location: 'Localisation',
    askingPrice: 'Prix Demandé',
    yearsInOperation: 'Années d\'Opération',
    sellerUrgency: 'Urgence Vendeur',
    urgencyHigh: 'Élevée',
    urgencyMedium: 'Moyenne',
    urgencyLow: 'Basse',
    financialMetrics: '💰 Métriques Financières',
    averageSDE: 'SDE Moyen',
    maintenanceCAPEX: 'CAPEX Maintenance',
    dscrSystem: 'DSCR (Système)',
    adjustedEBITDA: 'EBITDA Ajusté (3 dernières années):',
    riskFactors: '⚠️ Facteurs de Risque',
    mainWeakness: 'Faiblesse Principale:',
    customerConcentration: 'Concentration Client Principal:',
    managementScore: 'Score Management:',
    projectedGrowth: 'Croissance Projetée:',
    existingDebt: '💳 Dette Existante à Refinancer',
    description: 'Description',
    amount: 'Montant',
    secured: 'Sécurisée',
    yes: 'Oui',
    no: 'Non',
    
    // Step 2: Lender Info
    step2Title: 'Étape 2: Informations Prêteur',
    institutionInfo: '2.1 - Informations Institution',
    institutionName: 'Nom de l\'Institution',
    licenseNMLS: 'N° Licence / NMLS',
    representativeInfo: '2.2 - Informations Représentant',
    representativeName: 'Nom du Représentant',
    phone: 'Téléphone',
    email: 'Courriel',
    
    // Step 3: Loan Terms
    step3Title: 'Étape 3: Type & Termes du Prêt',
    lenderTypeProduct: '3.1 - Type de Prêteur & Produit',
    lenderType: 'Type de Prêteur',
    loanProductType: 'Type de Produit',
    selectOption: 'Sélectionnez...',
    commercialTerms: '3.2 - Termes Commerciaux',
    loanAmountOffered: 'Montant du Prêt Offert ($)',
    loanTerm: 'Durée (mois)',
    rateType: 'Type de Taux',
    rateFixed: 'Fixe',
    rateFloating: 'Variable',
    rateIndexed: 'Indexé (SOFR+)',
    baseRate: 'Taux de Base (%)',
    marginBps: 'Marge (bps)',
    marginNote: 'Si indexé (SOFR + X bps)',
    floorRate: 'Taux Plancher (%)',
    amortizationType: 'Type d\'Amortissement',
    amortLinear: 'Linéaire',
    amortInFine: 'In Fine (Ballon)',
    amortCustom: 'Calendrier Personnalisé',
    prepaymentPenalty: 'Pénalité de Remboursement Anticipé',
    prepaymentPlaceholder: 'Ex: 2% si remboursé dans les 3 premières années',
    
    // Step 4: Fees
    step4Title: 'Étape 4: Frais',
    structuredFees: 'Frais Structurés',
    arrangementFee: 'Frais d\'Arrangement (%)',
    feeOfLoan: '% du montant du prêt',
    drawFeeABL: 'Frais de Tirage ABL (%)',
    drawFeeNote: 'Si ABL, % par tirage',
    exitFee: 'Frais de Sortie (%)',
    exitFeeNote: 'Si Mezzanine/LBO',
    otherFees: 'Autres Frais',
    feeDescription: 'Description',
    feeAmount: 'Montant',
    feeType: 'Type',
    feeFixed: 'Fixe ($)',
    feePercentage: '% du prêt',
    addFee: '+ Ajouter un Frais',
    removeFee: 'Supprimer',
    
    // Step 5: Covenants
    step5Title: 'Étape 5: Covenants, Ratios & Garanties',
    maintenanceCovenants: 'Covenants de Maintenance',
    maintenanceCovenantsRequired: 'Covenants de maintenance requis? (Test trimestriel)',
    netDebtToEBITDA: 'Dette Nette / EBITDA Max',
    leverageRatio: 'Ratio de levier maximum',
    dscrMinimum: 'DSCR Minimum',
    dscrCalculationBase: 'Base de Calcul du DSCR',
    dscrCFADS: 'CFADS (Cash Flow After Debt Service)',
    dscrSDE: 'SDE (Seller\'s Discretionary Earnings)',
    dscrEBITDA: 'EBITDA',
    dividendRestrictions: 'Restrictions sur les Dividendes',
    dividendPlaceholder: 'Ex: Aucune distribution si DSCR < 1.5x',
    collateral: 'Garanties (Collateral)',
    collateralRequired: 'Garanties requises?',
    collateralTypes: 'Types de Garanties',
    personalGuaranteeRequired: 'Garantie personnelle (PG) requise?',
    realEstateConditioned: 'Offre conditionnée à l\'hypothèque immobilière?',
    
    // Step 6: ABL & Subordinated
    step6Title: 'Étape 6: ABL & Dette Subordonnée',
    ablSection: 'ABL (Asset-Based Lending)',
    isABLOffer: 'Ceci est une offre ABL?',
    arEligibility: 'Comptes Clients Éligibles (%)',
    inventoryEligibility: 'Inventaire Éligible (%)',
    cashSweep: 'Nantissement de Comptes (Cash Sweep) requis?',
    subordinatedDebt: 'Dette Subordonnée (Prêt Vendeur / Mezzanine)',
    isSubordinatedDebt: 'Ceci est une dette subordonnée?',
    subordinationToSenior: 'Subordonnée à la dette senior?',
    gracePeriod: 'Période de Carence (mois)',
    gracePeriodNote: 'Carence sur intérêts',
    balloonYear: 'Année du Ballon',
    balloonAmount: 'Montant Ballon ($)',
    
    // Step 7: Credit
    step7Title: 'Étape 7: Exigences de Crédit & International',
    nationalCreditScore: 'Score de Crédit National',
    creditScoreRequired: 'Score de crédit national requis?',
    minimumScore: 'Score Minimum',
    ficoNote: 'Score FICO typique: 300-850',
    foreignCreditScore: 'Score de Crédit Étranger',
    acceptForeignScores: 'Acceptez-vous des scores de crédit étrangers?',
    foreignMinimumScore: 'Score Étranger Minimum',
    foreignScoreNote: 'Score minimum accepté (échelle variable selon pays)',
    acceptedCountries: 'Pays Acceptés (codes ISO)',
    selectCountries: 'Sélectionnez les pays dont vous acceptez les scores de crédit:',
    countriesSelected: 'pays sélectionné(s)',
    foreignCollateral: 'Garanties Étrangères',
    acceptForeignCollateral: 'Acceptez-vous des garanties étrangères?',
    foreignCollateralDetails: 'Détails',
    foreignCollateralPlaceholder: 'Précisez les types acceptés, pays, conditions...',
    
    // Step 8: Review
    step8Title: 'Étape 8: Délais & Révision Finale',
    timeline: 'Échéancier',
    closingTimeline: 'Délai de Clôture (jours)',
    offerExpiration: 'Date d\'Expiration de l\'Offre',
    offerSummary: '📋 Résumé de Votre Offre',
    lender: 'Prêteur',
    amountOffered: 'Montant Offert',
    rate: 'Taux',
    type: 'Type',
    product: 'Produit',
    term: 'Durée',
    months: 'mois',
    toFill: '(À remplir)',
    toSelect: '(À sélectionner)',
    confirmSubmission: 'Je confirme que cette offre est complète et précise',
    confirmNote: 'Mon institution est prête à avancer avec ces termes.',
    
    // Validation errors
    errors: {
      institutionNameRequired: 'Nom de l\'institution requis',
      representativeNameRequired: 'Nom du représentant requis',
      emailRequired: 'Courriel requis',
      phoneRequired: 'Téléphone requis',
      lenderTypeRequired: 'Type de prêteur requis',
      loanProductRequired: 'Type de produit requis',
      loanAmountRequired: 'Montant du prêt requis',
      interestRateRequired: 'Taux d\'intérêt requis'
    },
    
    // Notifications
    draftSaved: '✅ Brouillon enregistré!',
    draftDeleted: '🗑️ Brouillon supprimé',
    draftRestored: '📝 Brouillon restauré!',
    saveError: '❌ Erreur sauvegarde',
    confirmDeleteDraft: 'Supprimer le brouillon sauvegardé?',
    draftFoundRestore: 'Un brouillon sauvegardé a été trouvé. Voulez-vous le restaurer?',
    offerSubmitted: '✅ Offre soumise avec succès!'
  }
};

export type LenderOfferTranslations = typeof LENDER_OFFER_TRANSLATIONS.en;
